import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, CircuitBoard, Gauge, LineChart, Layers, ShieldCheck, Zap, Users } from "lucide-react";
import { SiSap, SiSalesforce } from "react-icons/si";
import { BrandOracleMark } from "../components/site/BrandOracle";
import Reveal from "../components/site/Reveal";
import PlatformLogos from "../components/site/PlatformLogos";

const BENTOS = [
  {
    title: "Architect",
    body: "Target ERP architecture across SAP S/4HANA · BRIM, Oracle Fusion Cloud and Salesforce Revenue Cloud.",
    Icon: CircuitBoard,
    mono: "01 · architecture"
  },
  {
    title: "Implement",
    body: "Greenfield and brownfield ERP rollouts with senior functional and technical architects on every wave.",
    Icon: Layers,
    mono: "02 · implementation"
  },
  {
    title: "Operate",
    body: "Application management, post go-live stabilisation and continuous ERP optimisation.",
    Icon: Gauge,
    mono: "03 · operate"
  },
  {
    title: "Comply",
    body: "SOX, ASC 606 / IFRS 15, GDPR and data residency engineered into the ERP from day zero.",
    Icon: ShieldCheck,
    mono: "04 · compliance"
  },
];

const SERVICES = [
  { title: "ERP Strategy", body: "Maturity assessment, platform selection, business case and multi-year roadmap.", Icon: LineChart },
  { title: "ERP Implementation", body: "End-to-end delivery on SAP S/4HANA · BRIM, Oracle Fusion and Salesforce.", Icon: Layers },
  { title: "Managed Services (AMS)", body: "24×7 run, enhancements and release management post go-live.", Icon: Zap },
  { title: "OTC Staffing", body: "On-demand BRIM, Oracle and Salesforce Revenue Cloud specialists for your programme.", Icon: Users },
];


const Home = () => {
  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative min-h-[92vh] overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0 bg-grid-lg opacity-60" />
        <div className="absolute inset-0 bg-radial-cyan" />
        <div
          className="absolute inset-0 opacity-[0.22] mix-blend-screen"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1761912149936-8f662fc2a13e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMGRhdGElMjBhbmFseXRpY3MlMjBhYnN0cmFjdHxlbnwwfHx8fDE3NzY5NTg4ODR8MA&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-xp-bg" />

        <div className="relative container-xp pt-40 md:pt-48 pb-24">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <div className="eyebrow-lg mb-6">
              Enterprise ERP Consultancy · SAP · Oracle · Salesforce
            </div>
            <h1 className="font-display font-semibold tracking-display text-[44px] sm:text-6xl lg:text-7xl leading-[1.02]" data-testid="home-hero-title">
              ERP, engineered
              <br />
              <span className="text-xp-text">for </span>
              <span className="text-xp-cyan">Order-to-Cash.</span>
            </h1>
            <p className="mt-8 text-lg text-xp-muted max-w-2xl">
              <span className="brand">Xpace</span> is a boutique ERP consultancy specializing in mission-critical revenue platforms. We architect,
              implement, and operate solutions across <span className="text-xp-text">SAP S/4HANA &amp; BRIM</span>,{" "}
              <span className="text-xp-text">Oracle Fusion Cloud ERP</span>, and{" "}
              <span className="text-xp-text">Salesforce Revenue Cloud</span> — led by architects with deep functional and
              technical expertise across the entire stack.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/contact" className="btn-cyan" data-testid="home-hero-cta">
                Start a conversation <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link to="/solutions" className="btn-ghost" data-testid="home-hero-explore">
                Explore OTC solutions <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PARTNERS MARQUEE */}
      <section className="py-10 border-y border-white/5 bg-xp-surface/40">
        <div className="container-xp">
          <div className="flex items-center gap-6">
            <div className="eyebrow whitespace-nowrap">Expert in</div>
            <div className="flex-1"><PlatformLogos /></div>
          </div>
        </div>
      </section>

      {/* VALUE PROPOSITION — BENTO */}
      <section className="section-xp" data-testid="home-value">
        <div className="container-xp">
          <Reveal className="max-w-2xl mb-16">
            <div className="eyebrow-lg mb-4">Why <span className="brand">Xpace</span></div>
            <h2 className="font-display tracking-display text-[32px] md:text-5xl">
              One ERP consultancy. Three platforms. Zero hand-offs.
            </h2>
            <p className="mt-5 text-xp-muted max-w-xl">
              Most ERP boutiques specialise in one platform. <span className="brand">Xpace</span> runs SAP, Oracle and Salesforce under one roof — so your
              architecture is picked for your business, not our bench.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {BENTOS.map((b, i) => (
              <Reveal
                key={b.title}
                delay={i * 0.06}
                className={`card-xp group ${i === 0 ? "md:col-span-6" : "md:col-span-4"} ${i === 1 ? "md:col-span-6" : ""} ${
                  i === 3 ? "md:col-span-4" : ""
                }`}
                data-testid={`bento-${b.title.toLowerCase()}`}
              >
                <div className="flex items-start justify-between">
                  <div className="h-10 w-10 border border-white/10 flex items-center justify-center rounded-sm bg-xp-bg group-hover:border-xp-cyan/60 transition-colors">
                    <b.Icon className="h-5 w-5 text-xp-cyan" />
                  </div>
                  <div className="eyebrow">{b.mono}</div>
                </div>
                <h3 className="font-display text-2xl mt-8 tracking-display">{b.title}</h3>
                <p className="mt-3 text-xp-muted">{b.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="section-xp border-t border-white/5" data-testid="home-services-preview">
        <div className="container-xp">
          <Reveal className="flex items-end justify-between gap-6 mb-14 flex-wrap">
            <div className="max-w-xl">
              <div className="eyebrow-lg mb-4">Services</div>
              <h2 className="font-display tracking-display text-[32px] md:text-5xl">
                ERP consulting, end-to-end.
              </h2>
            </div>
            <Link to="/services" className="btn-ghost" data-testid="home-services-link">
              All services <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06} className="card-xp" data-testid={`service-card-${s.title.toLowerCase().replace(/\s+/g,'-')}`}>
                <div className="h-10 w-10 border border-white/10 flex items-center justify-center rounded-sm bg-xp-bg">
                  <s.Icon className="h-5 w-5 text-xp-cyan" />
                </div>
                <h3 className="font-display text-2xl mt-8 tracking-display">{s.title}</h3>
                <p className="mt-3 text-xp-muted">{s.body}</p>
                <div className="mt-8 pt-6 border-t border-white/5 flex items-center justify-between text-sm">
                  <span className="font-mono text-xp-dim">0{i + 1}</span>
                  <Link to="/services" className="text-xp-cyan hover:text-xp-text transition-colors flex items-center gap-1">
                    Learn more <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* OTC FOCUS */}
      <section className="section-xp" data-testid="home-otc">
        <div className="container-xp">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <Reveal className="lg:col-span-5">
              <div className="eyebrow-lg mb-4">Order-to-Cash Focus</div>
              <h2 className="font-display tracking-display text-[32px] md:text-5xl">
                Three ERPs. <br /> One revenue discipline.
              </h2>
              <p className="mt-5 text-xp-muted">
                Order-to-Cash is where ERPs earn their keep — and where most implementations quietly fail. <span className="brand">Xpace</span> has built
                a single, opinionated OTC methodology that works on SAP BRIM, Oracle Fusion and Salesforce Revenue Cloud.
              </p>
              <Link to="/solutions" className="btn-ghost mt-8" data-testid="home-otc-cta">
                See the OTC playbook <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Reveal>

            <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { Icon: SiSap, name: "SAP BRIM", modules: "SOM · CC · CI · FI-CA" },
                { Icon: BrandOracleMark, name: "Oracle", modules: "CPQ · Subscription · RMCS" },
                { Icon: SiSalesforce, name: "Salesforce", modules: "CPQ · Billing · RLM" }
              ].map(({ Icon, name, modules }, i) => (
                <Reveal key={name} delay={i * 0.08} className="card-xp" data-testid={`otc-card-${name.toLowerCase().replace(/\s+/g,'-')}`}>
                  <Icon className="h-8 w-8 text-xp-text" />
                  <div className="mt-8 font-display text-2xl tracking-display">{name}</div>
                  <div className="mt-1 font-mono text-xs text-xp-cyan">{modules}</div>
                  <div className="mt-8 h-px bg-white/5" />
                  <div className="mt-4 text-sm text-xp-muted">
                    Advisory · Delivery · Run
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="section-xp border-t border-white/5" data-testid="home-industries">
        <div className="container-xp">
          <Reveal className="flex items-end justify-between gap-6 mb-14 flex-wrap">
            <div className="max-w-xl">
              <div className="eyebrow-lg mb-4">Industries</div>
              <h2 className="font-display tracking-display text-[32px] md:text-5xl">
                ERP done right <br /> in the industries that pay for mistakes.
              </h2>
            </div>
            <p className="text-xp-muted max-w-sm text-sm">
              Our consultants have delivered ERP programmes in regulated, high-volume, high-complexity sectors — where
              downtime and revenue leakage are measured in millions per hour.
            </p>
          </Reveal>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-white/5 border border-white/5" data-testid="industries-grid">
            {[
              ["Telecommunications", "SAP BRIM · Convergent billing"],
              ["Software & SaaS", "Oracle CPQ · RMCS · Salesforce"],
              ["Energy & Utilities", "Revenue Cloud · Meter-to-cash"],
              ["Media & Entertainment", "Event-based charging"],
              ["Financial Services", "ASC 606 · SOX controls"],
              ["Manufacturing", "S/4HANA · Oracle Fusion ERP"],
            ].map(([name, sub], i) => (
              <Reveal
                key={name}
                delay={(i % 6) * 0.04}
                className="bg-xp-bg hover:bg-xp-surface transition-colors p-6 lg:p-8"
                data-testid={`industry-${name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                <div className="font-mono text-[10px] tracking-widest uppercase text-xp-dim">0{i + 1}</div>
                <div className="font-display text-lg mt-4 tracking-display text-xp-text">{name}</div>
                <div className="mt-2 text-xs text-xp-muted">{sub}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="section-xp border-t border-white/5" data-testid="home-team">
        <div className="container-xp">
          <Reveal className="flex items-end justify-between gap-6 mb-14 flex-wrap">
            <div className="max-w-xl">
              <div className="eyebrow-lg mb-4">Our People</div>
              <h2 className="font-display tracking-display text-[32px] md:text-5xl">
                Senior architects. <br /> Every engagement.
              </h2>
            </div>
            <p className="text-xp-muted max-w-sm text-sm">
              No staffing pyramid. No A-team for the sales pitch and B-team for delivery — you get our principals working
              shoulder-to-shoulder with your team.
            </p>
          </Reveal>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5" data-testid="team-grid">
            {[1, 2, 3, 4, 5].map((n, i) => (
              <Reveal
                key={n}
                delay={i * 0.06}
                className="group"
                data-testid={`team-${n}`}
              >
                <div className="relative aspect-[3/4] overflow-hidden border border-white/5 rounded-sm bg-xp-surface">
                  <img
                    src={`/team/0${n}.png`}
                    alt="Xpace consultant at work"
                    loading="lazy"
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-xp-bg via-xp-bg/30 to-transparent" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-xp">
        <div className="container-xp">
          <Reveal className="relative overflow-hidden border border-white/10 rounded-sm p-10 md:p-16 bg-xp-surface">
            <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
            <div className="absolute -top-10 -right-10 h-56 w-56 rounded-full bg-xp-cyan/20 blur-3xl pointer-events-none" />
            <div className="relative grid md:grid-cols-5 gap-10 items-center">
              <div className="md:col-span-3">
                <div className="eyebrow mb-4">Ready when you are</div>
                <h3 className="font-display text-3xl md:text-5xl tracking-display">
                  Let's rebuild your revenue engine.
                </h3>
              </div>
              <div className="md:col-span-2 flex md:justify-end">
                <Link to="/contact" className="btn-cyan" data-testid="home-footer-cta">
                  Book a discovery call <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
